import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import React from 'react';
import { FinancialInformationFormatted } from '../../api/types';
import {
    BANK_ACCOUNTS,
    DocumentRequestStatusTypes,
    FINANCE_SETTINGS,
    LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    MAKE_SURE_YOU_GET_PAID,
} from '../../constants';
import { NotificationMessageType } from '../../types';
import {
    getLocalizedDate,
    getLocalizedPayoutAmount,
    getLocalStorageItemWithExpiry,
    getNotificationMessage,
} from '../../utils';
import {
    ModalFinancialMissingInformation,
    ModalTaxpayerMissingInformation,
} from '../types';
import { getFinancialMissingInfoData } from './getFinancialMissingInfoData';
import { getTaxPayerMissingInfoData } from './getTaxPayerMissingInfoData';

export const getSessionStorageOrDefault = (key: string, defaultValue: any) => {
    const stored = getWindowObject('sessionStorage')?.getItem(key);
    if (!stored) {
        return defaultValue;
    }
    return JSON.parse(stored);
};

export const isAddBankAccountFormModalOpen = () => {
    const { pathname, search } = getWindowObject('location') || {};
    const isBankAccountsPage = pathname.includes(
        `${FINANCE_SETTINGS}/${BANK_ACCOUNTS}`,
    );
    const queryParams = new URLSearchParams(search);
    const isModalOpen = queryParams.get('openModal') === 'true';

    return isBankAccountsPage && isModalOpen;
};

export const getPayoutMethodRequestEventEndedModalNotificationMessage = ({
    currency,
    endDate,
    eventName,
    locale,
    payoutAmount,
}: NotificationMessageType) => {
    const localizedPayoutAmount = getLocalizedPayoutAmount(
        locale,
        payoutAmount,
        currency,
    );
    const formattedEndDate = getLocalizedDate(endDate, locale);
    const description = (
        <>
            <p>
                {gettext(
                    'To receive your %(localizedPayoutAmount)s payout from your event that ended on %(formattedEndDate)s, add a bank account.',
                    {
                        localizedPayoutAmount,
                        formattedEndDate,
                    },
                )}
            </p>
            <p>
                {gettext(
                    "We can't deliver your funds until you add a bank account.",
                )}
            </p>
        </>
    );
    if (payoutAmount > 0) {
        const { title } = getNotificationMessage({
            currency,
            endDate,
            eventName,
            locale,
            payoutAmount,
        });
        return { title, description };
    }

    const title = MAKE_SURE_YOU_GET_PAID;

    return { title, description };
};

export const getMissingInformationObject = async (
    data: FinancialInformationFormatted,
): Promise<
    ModalTaxpayerMissingInformation | ModalFinancialMissingInformation | {}
> => {
    // TODO: Don't use local storage with expiry to store taxpayer information status
    // TODO: Handle the taxpayer info status directly from the API without the current delayed time
    const storedStatus = getLocalStorageItemWithExpiry(
        LOCALLY_STORED_TAXPAYER_REQUEST_STATUS_KEY,
    );
    const documentRequestStatus =
        data?.taxpayerInformation?.documentRequestStatus;

    if (
        documentRequestStatus &&
        documentRequestStatus !== DocumentRequestStatusTypes.FULFILLED &&
        storedStatus !== DocumentRequestStatusTypes.FULFILLED
    ) {
        return getTaxPayerMissingInfoData(documentRequestStatus);
    }

    const financialMissingInformationData = await getFinancialMissingInfoData(
        data,
        storedStatus,
    );
    return financialMissingInformationData;
};
